const toNumericId = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (/^-?\d+$/.test(trimmedValue)) {
      const numericValue = Number(trimmedValue);
      return Number.isFinite(numericValue) ? numericValue : null;
    }
  }

  return null;
};

const toNumericIdList = (values = []) => {
  return values.reduce((acc, value) => {
    const normalized = toNumericId(value);
    if (normalized === null || acc.includes(normalized)) {
      return acc;
    }
    acc.push(normalized);
    return acc;
  }, []);
};

const GOALKEEPER_INTERCEPTION_ALIASES = {
  779: { fallbackId: 152, targetLexiconId: 635 },
  780: { fallbackId: 153, targetLexiconId: 636 },
  781: { fallbackId: 155, targetLexiconId: 638 },
};

const normalizeName = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .toLowerCase()
    .trim()
    .replace(/[-_/]/g, " ")
    .replace(/[.,%]/g, "")
    .replace(/\s+/g, " ");
};

const getStatisticLexiconId = (statistic) => {
  if (!statistic || typeof statistic !== "object") {
    return null;
  }

  return toNumericId(
    statistic.lexic_id ??
      statistic.lexic ??
      statistic.lexc ??
      statistic.lexicon_id ??
      statistic.lexicon ??
      null
  );
};

const getStatisticName = (statistic) => {
  if (!statistic || typeof statistic !== "object") {
    return "";
  }

  return (
    statistic.parameter_name ||
    statistic.parameter ||
    statistic.name ||
    statistic.title ||
    ""
  );
};

const getParameterName = (parameter) => {
  if (!parameter || typeof parameter !== "object") {
    return "";
  }

  return (
    parameter?.parameter_name ||
    parameter?.parameter ||
    parameter?.name ||
    parameter?.title ||
    ""
  );
};

const getLexiconIdCandidates = (labelConfig, getPlayerParameterLexiconId) => {
  if (!labelConfig || typeof labelConfig !== "object") {
    return [];
  }

  const preferredLexiconId = toNumericId(labelConfig.preferredLexiconId);
  const preferredTextLexiconId =
    labelConfig.preferredText && getPlayerParameterLexiconId
      ? toNumericId(getPlayerParameterLexiconId(labelConfig.preferredText))
      : null;
  const fallbackLexiconId = toNumericId(labelConfig.fallbackLexiconId);

  return toNumericIdList([
    preferredLexiconId,
    preferredTextLexiconId,
    fallbackLexiconId,
  ]);
};

export const findStatisticById = (statistics, parameterId) => {
  if (!Array.isArray(statistics) || statistics.length === 0) {
    return null;
  }

  const normalizedParameterId = toNumericId(parameterId);
  if (normalizedParameterId === null) {
    return null;
  }

  return (
    statistics.find((item) => {
      const currentParameterId = toNumericId(
        item?.parameter_id ??
          item?.id ??
          item?.parameterId ??
          item?.param_id ??
          null
      );
      return currentParameterId === normalizedParameterId;
    }) || null
  );
};

export const resolveStatisticByIdOrLexicon = ({
  statistics,
  parameterId,
  labelConfig,
  getPlayerParameterLexiconId,
  lexiconToParameterIdMap = {},
}) => {
  const byIdStatistic = findStatisticById(statistics, parameterId);
  if (byIdStatistic) {
    return byIdStatistic;
  }

  const lexiconIdCandidates = getLexiconIdCandidates(
    labelConfig,
    getPlayerParameterLexiconId
  );
  if (!Array.isArray(statistics)) {
    return null;
  }

  for (let i = 0; i < lexiconIdCandidates.length; i += 1) {
    const lexiconId = lexiconIdCandidates[i];
    const mappedParameterId = toNumericId(
      lexiconToParameterIdMap[lexiconId]
    );
    if (mappedParameterId !== null) {
      const byMappedIdStatistic = findStatisticById(statistics, mappedParameterId);
      if (byMappedIdStatistic) {
        return byMappedIdStatistic;
      }
    }
  }

  for (let i = 0; i < lexiconIdCandidates.length; i += 1) {
    const lexiconId = lexiconIdCandidates[i];
    const byLexiconAsParameterId = findStatisticById(statistics, lexiconId);
    if (byLexiconAsParameterId) {
      return byLexiconAsParameterId;
    }
  }

  for (let i = 0; i < lexiconIdCandidates.length; i += 1) {
    const lexiconId = lexiconIdCandidates[i];
    const byLexiconStatistic = statistics.find((item) => getStatisticLexiconId(item) === lexiconId);
    if (byLexiconStatistic) {
      return byLexiconStatistic;
    }
  }

  const normalizedPreferredName = normalizeName(labelConfig?.preferredText);
  const normalizedFallbackName = normalizeName(labelConfig?.fallbackText);
  const normalizedCandidates = [
    normalizedPreferredName,
    normalizedFallbackName,
  ].filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return null;
  }

  return (
    statistics.find((item) =>
      normalizedCandidates.includes(normalizeName(getStatisticName(item)))
    ) || null
  );
};

export const buildParameterGroupIdList = (groups = []) => {
  if (!Array.isArray(groups) || groups.length === 0) {
    return [];
  }

  return toNumericIdList(
    groups.map((group) => group?.id).filter((groupId) => groupId !== undefined)
  );
};

export const buildLexiconToParameterIdMap = (
  parameters = [],
  resolveLexiconId = null
) => {
  return parameters.reduce((acc, parameter) => {
    const parameterId = toNumericId(
      parameter?.id ??
        parameter?.parameter_id ??
        parameter?.parameterId ??
        parameter?.param_id
    );
    const directLexiconId = toNumericId(
      parameter?.lexic_id ??
        parameter?.lexic ??
        parameter?.lexc ??
        parameter?.lexicon_id ??
        parameter?.lexicon
    );
    const resolvedLexiconId =
      directLexiconId ??
      (typeof resolveLexiconId === "function"
        ? toNumericId(resolveLexiconId(getParameterName(parameter)))
        : null);
    const lexiconId = resolvedLexiconId;

    if (parameterId === null || lexiconId === null || acc[lexiconId]) {
      return acc;
    }

    acc[lexiconId] = parameterId;
    return acc;
  }, {});
};

export const remapEventsParamsWithLiveIds = (
  baseParamsList = [],
  lexiconToParameterIdMap = {}
) => {
  if (!Array.isArray(baseParamsList) || baseParamsList.length === 0) {
    return [];
  }

  return baseParamsList.map((group) => {
    if (!Array.isArray(group?.actions) || group.actions.length === 0) {
      return group;
    }

    const nextActions = group.actions.map((action) => {
      if (action?.team_parameter_id || action?.teamParameterId) {
        return action;
      }

      const actionLexiconId = toNumericId(action?.lexic_id);
      const mappedId =
        actionLexiconId !== null
          ? toNumericId(lexiconToParameterIdMap[actionLexiconId])
          : null;
      const currentParameterId = toNumericId(
        action?.parameter_id ?? action?.parameterId ?? null
      );
      const effectiveParameterId = mappedId ?? currentParameterId;

      if (
        effectiveParameterId === null ||
        effectiveParameterId === currentParameterId
      ) {
        return action;
      }

      return {
        ...action,
        parameter_id: effectiveParameterId,
      };
    });

    return {
      ...group,
      actions: nextActions,
    };
  });
};

export const applyGoalkeeperInterceptionEventAliases = (
  baseParamsList = [],
  lexiconToParameterIdMap = {}
) => {
  if (!Array.isArray(baseParamsList) || baseParamsList.length === 0) {
    return [];
  }

  return baseParamsList.map((group) => {
    if (!Array.isArray(group?.actions) || group.actions.length === 0) {
      return group;
    }

    let didChangeGroup = false;

    const nextActions = group.actions.map((action) => {
      const actionLexiconId = toNumericId(action?.lexic_id);
      const aliasConfig =
        actionLexiconId !== null
          ? GOALKEEPER_INTERCEPTION_ALIASES[actionLexiconId]
          : null;

      if (!aliasConfig) {
        return action;
      }

      const mappedId = toNumericId(
        lexiconToParameterIdMap[aliasConfig.targetLexiconId]
      );
      const effectiveId = mappedId ?? aliasConfig.fallbackId;

      if (effectiveId === action?.id) {
        return action;
      }

      didChangeGroup = true;

      return {
        ...action,
        id: effectiveId,
      };
    });

    if (!didChangeGroup) {
      return group;
    }

    return {
      ...group,
      actions: nextActions,
    };
  });
};
